import { useEffect, useMemo, useRef, useState } from 'react'

/** Tile piramidi tanımı — /api/maps/list içinden gelir. */
export interface TileConfig {
  baseUrl: string   // '/maps/tiles/zesty'
  maxLevel: number  // en üst seviye; 2^maxLevel * tileSize piksel (7 → 32768)
  tileSize: number  // 256
  /**
   * Tile içeriğinin sürümü, URL'e `?v=` olarak eklenir. nginx tile'ları
   * `immutable` ile bir yıl cache'lettiğinden aynı URL'de içerik değiştirmek
   * mevcut kullanıcılarda görünmez kalırdı; tile'lar yeniden üretildiğinde bu
   * değer değişir ve önbellek doğal olarak atlanır.
   */
  version?: string
}

interface Props {
  tiles: TileConfig | null
  /** Tile'lar yüklenene kadar altta duran tek parça görüntü. */
  base: string | null
  /** Fabric viewport transform'u: ekran = dünya * a + (e, f) */
  vpt: { a: number; e: number; f: number }
  /** Fabric dünyasının kenar uzunluğu (kare). */
  worldSize: number
  /**
   * Zemine uygulanacak CSS filtresi (taktiksel görünüm); bkz. `lib/mapView.ts`.
   *
   * Bileşenin *kök* elemanına verilir — yani yalnızca harita zeminini etkiler.
   * Marker'lar, çizimler ve Fabric canvas'ı bu ağacın dışında olduğu için
   * dokunulmadan kalır: zemin solar, üstündekiler tam doygunlukta durur.
   *
   * Kök, viewport boyutunda ve zoom'dan bağımsız sabit bir kutu olduğu için
   * filtre kare başına yeniden çözülmez; transform edilen dünya katmanına
   * verilseydi her zoom karesinde yüzlerce tile için yeniden hesaplanırdı.
   */
  filter?: string
  /**
   * Yalnızca bu dünya dikdörtgeni çizilir (oynanabilir alan). Piramit haritanın
   * kenarını, denizi, gidilemeyen araziyi de kapsıyor; dışarıdaki tile'lar hiç
   * istenmez, `base` ve sınırı taşan tile'lar da dikdörtgenden kırpılır.
   */
  clip?: { x: number; y: number; w: number; h: number } | null
  /**
   * Dünya katmanının dönüşümü kapsayıcıdaki canlı CSS değişkenlerinden
   * (`--va`, `--ve`, `--vf`) okunur: zoom/pan React render'ı beklemeden her
   * karede izlenir, `vpt` yalnızca hangi tile'ların isteneceğini belirler.
   * Strateji editörü kullanır (bkz. `pinAt`).
   */
  live?: boolean
  /**
   * Canlı viewport'a abonelik. Verilirse hangi tile'ların isteneceği hareket
   * sürerken de (en çok `LIVE_CULL_MS`'de bir) güncellenir — yalnızca bu katman
   * render olur, editör değil. Yoksa uzun bir kaydırmada yeni açılan alan
   * hareket durana kadar alttaki `base` görüntüsüyle kalırdı.
   */
  subscribe?: (fn: (v: { a: number; e: number; f: number }) => void) => () => void
}

const LIVE_CULL_MS = 120

interface TileId { z: number; x: number; y: number }

const key = (t: TileId) => `${t.z}/${t.x}/${t.y}`

/**
 * Seviye değişimi için ölü bant. İdeal seviye (log2 ölçeğinde) mevcut seviyeden
 * 0.5'ten fazla uzaklaşınca yuvarlama seviyeyi değiştirir; sınırın tam üstünde
 * gezinen bir zoom bu yüzden iki seviye arasında gidip gelebilir. Ek pay
 * bırakarak o titremeyi engelliyoruz.
 */
const LEVEL_HYSTERESIS = 0.18

/**
 * Görünür alandaki tile'ları hesaplar. Kenarlarda `MARGIN` tile'lık pay bırakılır:
 * tile'lar dünya koordinatlarında sabit durduğu için fazladan tile tutmanın
 * layout maliyeti yok, buna karşılık pan/zoom sırasında set daha seyrek değişir —
 * yani daha az mount/unmount, daha az pop-in.
 */
const MARGIN = 2

/**
 * Yedek katmanın tile sayısı için son çare üst sınırı.
 *
 * Yedek, hedef seviye tamamlanana kadar altta duran *önceki* seviyedir ve
 * **mevcut** viewport'a göre hesaplanır. Uzaklaşırken hedef seviye hemen düşer
 * ama yedek yüksek seviyede kalır; görünür dünya alanı da büyüdüğü için yedeğin
 * kapsadığı tile sayısı katlanarak artar — z7'deyken tüm haritaya uzaklaşmak
 * 128×128 = 16384 `<img>` demektir. Donmanın sebebi buydu.
 *
 * Asıl koruma sayı sınırı değil, yedeği *zaten yüklenmiş* tile'larla
 * sınırlamak (bkz. `fallback`): yedek katmanın işi, yeni seviye gelene kadar az
 * önce bakılan alanı kapalı tutmak; hiç yüklenmemiş bir tile bunu zaten
 * yapamaz, sadece yeni bir istek açar. Bu filtre kümeyi doğal olarak "biraz
 * önce ekranda olan" tile'lara indirir. Sınır, çok gezilmiş bir oturumda
 * birikmiş yüklü tile'lara karşı emniyet supabıdır; aşılırsa yedek atlanır ve
 * altta duran `base` görüntüsü devreye girer, yani boşluk yine oluşmaz.
 */
const MAX_FALLBACK_TILES = 600

/**
 * Seviye seçiminde kullanılacak cihaz piksel oranı; 2 ile sınırlı.
 *
 * Tile seviyesi CSS pikseline göre değil *fiziksel* ekran pikseline göre
 * seçilmelidir. 2× bir ekranda her CSS pikseli iki fiziksel piksele karşılık
 * gelir; CSS ölçüsüyle seçilen seviye bu yüzden tam bir kademe düşük kalır ve
 * tarayıcı tile'ı büyüterek gösterir. Haritanın "yumuşak" görünmesinin asıl
 * sebebi buydu.
 *
 * Tavanı 2'de tutuyoruz: 3× ekranda bir kademe daha çıkmak ekrandaki tile
 * sayısını dokuz katına çıkarır, karşılığında gözle ayırt edilir bir kazanç
 * yok. (Leaflet'in `detectRetina`'sı da ikili bir bayrak — aynı sınır.)
 *
 * Tarayıcı zoom'u DPR'ı değiştirdiği için değer dinlenir: `matchMedia` ile
 * kurulan sorgu mevcut orana bağlıdır, eşleşme bozulduğunda yeni orana göre
 * yeniden abone olunur.
 */
const MAX_DPR = 2

function useDevicePixelRatio(): number {
  const read = () =>
    typeof window === 'undefined' ? 1 : Math.min(MAX_DPR, window.devicePixelRatio || 1)

  const [dpr, setDpr] = useState(read)

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    let mql: MediaQueryList | null = null
    let cancelled = false

    const onChange = () => {
      if (cancelled) return
      setDpr(read())
      subscribe()
    }
    const subscribe = () => {
      if (cancelled) return
      mql = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`)
      mql.addEventListener('change', onChange, { once: true })
    }

    subscribe()
    return () => {
      cancelled = true
      mql?.removeEventListener('change', onChange)
    }
  }, [])

  return dpr
}

function visibleTiles(
  z: number,
  worldSize: number,
  vpt: { a: number; e: number; f: number },
  viewW: number,
  viewH: number,
  clip?: { x: number; y: number; w: number; h: number } | null,
): TileId[] {
  const n = 2 ** z
  const tileWorld = worldSize / n
  if (!(tileWorld > 0) || !(vpt.a > 0)) return []

  // Ekran kutusunu dünya koordinatına çevir: dünya = (ekran - offset) / a
  const cx0 = clip ? Math.max(0, clip.x) : 0
  const cy0 = clip ? Math.max(0, clip.y) : 0
  const cx1 = clip ? Math.min(worldSize, clip.x + clip.w) : worldSize
  const cy1 = clip ? Math.min(worldSize, clip.y + clip.h) : worldSize
  const left   = Math.max(cx0, (0     - vpt.e) / vpt.a)
  const right  = Math.min(cx1, (viewW - vpt.e) / vpt.a)
  const top    = Math.max(cy0, (0     - vpt.f) / vpt.a)
  const bottom = Math.min(cy1, (viewH - vpt.f) / vpt.a)
  if (right <= left || bottom <= top) return []

  // Pay da kırpmanın dışına taşmaz: oynanabilir alanın dışı hiç istenmez.
  const tx0 = Math.floor(cx0 / tileWorld), tx1 = Math.min(n - 1, Math.ceil(cx1 / tileWorld) - 1)
  const ty0 = Math.floor(cy0 / tileWorld), ty1 = Math.min(n - 1, Math.ceil(cy1 / tileWorld) - 1)
  const x0 = Math.max(tx0, Math.floor(left   / tileWorld) - MARGIN)
  const x1 = Math.min(tx1, Math.ceil (right  / tileWorld) + MARGIN)
  const y0 = Math.max(ty0, Math.floor(top    / tileWorld) - MARGIN)
  const y1 = Math.min(ty1, Math.ceil (bottom / tileWorld) + MARGIN)

  const out: TileId[] = []
  for (let y = y0; y <= y1; y++)
    for (let x = x0; x <= x1; x++)
      out.push({ z, x, y })
  return out
}

/**
 * Harita zeminini tile piramidi olarak çizer.
 *
 * Fabric canvas'ının *altında*, aynı viewport transform'unu kullanarak konumlanır —
 * marker overlay'leriyle birebir aynı desen. Yakınlaştıkça daha yüksek seviyeli
 * (daha detaylı) tile'lara geçilir.
 *
 * Titremeyi ve takılmayı önleyen beş kural:
 *
 * 1. **Tek transform edilen dünya katmanı.** Tile'lar dünya koordinatlarında
 *    sabit durur; zoom/pan yalnızca kapsayıcının `transform`'unu değiştirir.
 *    Önceki sürümde her karede ~60 `<img>`'in left/top/width/height'ı yeniden
 *    yazılıyordu — kare başına yüzlerce stil mutasyonu ve tam layout. Asıl
 *    titreme kaynağı buydu.
 * 2. **Her seviye kendi kalıcı kabında.** Kaplar `z` ile key'lenir, böylece bir
 *    seviye "hedef"ten "yedek"e geçerken React `<img>` elemanlarını yok edip
 *    yeniden yaratmaz.
 * 3. **Yedek katman erken kaldırılmaz — ama yalnızca yüklenmiş tile'lardan
 *    oluşur.** Son tam yüklenen seviye hedef tamamlanana kadar altta durur;
 *    uzaklaşırken patlamaması için `loadedRef` ile sınırlanır, bkz.
 *    `MAX_FALLBACK_TILES`.
 * 4. **Seviye seçiminde ölü bant.** Sınırın tam üstünde gezinen zoom iki seviye
 *    arasında salınmaz.
 * 5. **Yükleme bildirimleri kare başına toplanır.** `markLoaded` sadece ref'i
 *    günceller; render'ı tetikleyen `bump` bir rAF içinde birleşir.
 *
 * En altta her zaman `base` görüntüsü durur, yani hiçbir anda tamamen boş alan
 * oluşmaz. Zemin rengi `filter` prop'uyla değiştirilebilir (taktiksel görünüm).
 */
export default function MapTileLayer({ tiles, base, vpt: committedVpt, worldSize, filter, clip, live, subscribe }: Props) {
  const [liveVpt, setLiveVpt] = useState<{ a: number; e: number; f: number } | null>(null)
  useEffect(() => {
    if (!subscribe) return
    let pending: { a: number; e: number; f: number } | null = null
    let timer: ReturnType<typeof setTimeout> | null = null
    const unsub = subscribe(v => {
      pending = v
      if (timer !== null) return
      timer = setTimeout(() => { timer = null; setLiveVpt(pending) }, LIVE_CULL_MS)
    })
    return () => { unsub(); if (timer !== null) clearTimeout(timer) }
  }, [subscribe])
  // Abonelik son değeri de bildirir; kaydedilen değer onun gerisinde kalamaz.
  const vpt = liveVpt ?? committedVpt
  const hostRef = useRef<HTMLDivElement>(null)
  const [view, setView] = useState({ w: 0, h: 0 })

  useEffect(() => {
    const el = hostRef.current
    if (!el) return
    const measure = () => {
      const r = el.getBoundingClientRect()
      setView(v => (v.w === r.width && v.h === r.height ? v : { w: r.width, h: r.height }))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const dpr = useDevicePixelRatio()

  // Haritanın ekranda kapladığı *fiziksel* piksel genişliği tileSize'ın kaç
  // katı — kesirli seviye. `vpt.a` CSS pikseli verdiği için DPR ile çarpılır;
  // bkz. useDevicePixelRatio.
  const idealLevel = useMemo(() => {
    if (!tiles) return 0
    const rendered = worldSize * vpt.a * dpr
    if (!(rendered > 0)) return 0
    const raw = Math.log2(rendered / tiles.tileSize)
    return Math.min(tiles.maxLevel, Math.max(0, raw))
  }, [tiles, worldSize, vpt.a, dpr])

  const [level, setLevel] = useState(() => Math.round(idealLevel))
  useEffect(() => {
    setLevel(cur =>
      Math.abs(idealLevel - cur) > 0.5 + LEVEL_HYSTERESIS ? Math.round(idealLevel) : cur,
    )
  }, [idealLevel])

  const loadedRef = useRef<Set<string>>(new Set())
  const [, bump] = useState(0)

  // Görünür kümesi eksiksiz yüklenmiş en son seviye — hedef hazır olmadığı sürece
  // altta durur.
  const [fallbackLevel, setFallbackLevel] = useState<number | null>(null)

  // Harita değişince yükleme hafızası sıfırlanmalı, yoksa eski haritanın
  // tile'ları yüklenmiş sayılır.
  useEffect(() => {
    loadedRef.current = new Set()
    setFallbackLevel(null)
  }, [tiles?.baseUrl])

  const target = useMemo(
    () => (tiles ? visibleTiles(level, worldSize, vpt, view.w, view.h, clip) : []),
    [tiles, level, worldSize, vpt, view.w, view.h, clip],
  )

  const targetComplete =
    target.length > 0 && target.every(t => loadedRef.current.has(key(t)))

  useEffect(() => {
    if (targetComplete && fallbackLevel !== level) setFallbackLevel(level)
  }, [targetComplete, fallbackLevel, level])

  /**
   * Tile yüklendi işaretle. Yükleme bilgisi ref'te tutulur; render'ı tetikleyen
   * `bump` kare başına bir kez, requestAnimationFrame ile toplanır.
   *
   * Aksi halde ekrandaki her tile kendi `onLoad`'unda ayrı bir render açardı —
   * DPR düzeltmesinden sonra görünümde ~360 tile olabildiği için bu, her biri
   * iki `visibleTiles` hesabı yapıp yüzlerce `<img>`'i uzlaştıran 360 render
   * demekti. Zoom sırasındaki takılmanın kaynağı buydu.
   */
  const rafRef = useRef<number | null>(null)
  const markLoaded = (t: TileId) => {
    const k = key(t)
    if (loadedRef.current.has(k)) return
    loadedRef.current.add(k)
    if (rafRef.current !== null) return
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null
      bump(n => n + 1)
    })
  }

  useEffect(() => () => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
  }, [])

  const fallback = useMemo(() => {
    if (!tiles || fallbackLevel === null || fallbackLevel === level) return []
    // Yalnızca yüklenmiş tile'lar: yedeğin işi az önce bakılan alanı kapalı
    // tutmak, yeni istek açmak değil. Uzaklaşırken bu filtre kümeyi patlamaktan
    // kurtarır; bkz. MAX_FALLBACK_TILES.
    //
    // `loadedRef` mutasyonu memo'yu yeniden hesaplatmaz, ama önemli olan an
    // seviyenin/viewport'un değiştiği andır — memo zaten orada tazelenir.
    const ids = visibleTiles(fallbackLevel, worldSize, vpt, view.w, view.h, clip)
      .filter(t => loadedRef.current.has(key(t)))
    return ids.length > MAX_FALLBACK_TILES ? [] : ids
  }, [tiles, fallbackLevel, level, worldSize, vpt, view.w, view.h, clip])

  // Alttan üste: yedek seviye, sonra hedef seviye. Her seviye kendi kalıcı
  // kabında durduğu için rol değişimi remount'a yol açmaz.
  const layers: { z: number; ids: TileId[]; track: boolean }[] = []
  if (fallback.length > 0 && fallbackLevel !== null) {
    layers.push({ z: fallbackLevel, ids: fallback, track: false })
  }
  if (target.length > 0) layers.push({ z: level, ids: target, track: true })

  return (
    <div
      ref={hostRef}
      className="absolute inset-0 overflow-hidden pointer-events-none select-none"
      // Mod değişiminde ani sıçrama olmasın; zoom/pan'i etkilemez çünkü filtre
      // değeri yalnızca mod değiştiğinde değişir.
      style={{ filter, transition: 'filter 250ms ease' }}
    >
      {/*
        Tek transform edilen "dünya" katmanı. İçindeki her şey dünya
        koordinatlarında (0..worldSize) sabit durur; zoom/pan yalnızca bu div'in
        transform'unu değiştirir.

        Bu, titremenin asıl çözümü: eskiden her zoom karesinde ~60 <img>'in
        left/top/width/height'ı yeniden yazılıyordu — kare başına yüzlerce stil
        mutasyonu, yani tam layout + repaint. Şimdi kare başına tek bir transform
        değişiyor, tile elemanlarına hiç dokunulmuyor.
      */}
      <div
        className="absolute"
        style={{
          left: 0,
          top: 0,
          // Boyut şart: Tailwind preflight img'lere max-width:100% veriyor, o da
          // kapsayıcının genişliğine göre çözülüyor. Boyutsuz (shrink-to-fit,
          // yani 0 genişlikte) bir kapsayıcıda tüm tile'lar sıfıra çöker.
          width: worldSize,
          height: worldSize,
          transformOrigin: '0 0',
          transform: live
            ? `translate(calc(var(--ve, ${committedVpt.e}) * 1px), calc(var(--vf, ${committedVpt.f}) * 1px)) scale(var(--va, ${committedVpt.a}))`
            : `translate(${vpt.e}px, ${vpt.f}px) scale(${vpt.a})`,
          // Kırpma dünya biriminde: clip-path transform'dan önce, kapsayıcının
          // kendi koordinatında çözülür, zoom'la yeniden hesaplanmaz.
          clipPath: clip
            ? `inset(${clip.y}px ${worldSize - clip.x - clip.w}px ${worldSize - clip.y - clip.h}px ${clip.x}px)`
            : undefined,
        }}
      >
        {base && (
          <img
            src={base}
            alt=""
            draggable={false}
            className="absolute select-none"
            style={{ left: 0, top: 0, width: worldSize, height: worldSize, maxWidth: 'none' }}
          />
        )}
        {tiles && layers.map(({ z, ids, track }) => {
          const tileWorld = worldSize / 2 ** z
          const v = tiles.version ? `?v=${encodeURIComponent(tiles.version)}` : ''
          // Binde birkaçlık taşma: komşu tile'lar arasında saç teli çizgi kalmasın.
          // Dünya biriminde sabit olduğu için zoom'la yeniden hesaplanmaz.
          const size = tileWorld * 1.004
          return (
            <div key={z} className="absolute" style={{ left: 0, top: 0, width: worldSize, height: worldSize }}>
              {ids.map(t => (
                <img
                  key={key(t)}
                  src={`${tiles.baseUrl}/${t.z}/${t.x}/${t.y}.webp${v}`}
                  alt=""
                  draggable={false}
                  decoding="async"
                  className="absolute select-none"
                  style={{
                    left: t.x * tileWorld,
                    top:  t.y * tileWorld,
                    width: size,
                    height: size,
                    maxWidth: 'none',
                  }}
                  onLoad={track ? () => markLoaded(t) : undefined}
                  onError={track ? () => markLoaded(t) : undefined}
                  ref={track ? (el => { if (el?.complete) markLoaded(t) }) : undefined}
                />
              ))}
            </div>
          )
        })}
      </div>
    </div>
  )
}
